import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { nombreProyectoDesdeLista } from '../../../utils/projectsFromApi';
import {
  formatearRangoFechas,
  formatearFechaHora,
  etiquetaSemana,
} from '../../../data/mockCiclosCiclicos';
import { eliminarSalidasPorCicloId } from '../../../data/mockSalidasProduccion';
import {
  fetchDatosPlaneamientoCiclicos,
  deleteCiclico,
  migrarCiclosLocalesAlServidor,
} from '../../../services/ciclicoService';

const CiclicosIndex = () => {
  const [ciclos, setCiclos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [datosLoading, setDatosLoading] = useState(true);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroProyectoId, setFiltroProyectoId] = useState('');
  const [filtroPlantillaId, setFiltroPlantillaId] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroNumeroSemana, setFiltroNumeroSemana] = useState('');
  const [plantillas, setPlantillas] = useState([]);

  useEffect(() => {
    let ok = true;
    (async () => {
      setDatosLoading(true);
      try {
        let bundle = await fetchDatosPlaneamientoCiclicos();
        if (ok && bundle.ciclos.length === 0) {
          await migrarCiclosLocalesAlServidor();
          bundle = await fetchDatosPlaneamientoCiclicos();
        }
        if (ok) {
          setProyectos(bundle.proyectos);
          setPlantillas(bundle.plantillas);
          setCiclos(bundle.ciclos);
        }
      } catch {
        if (ok) {
          setProyectos([]);
          setPlantillas([]);
          setCiclos([]);
        }
      } finally {
        if (ok) setDatosLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const aniosEnDatos = useMemo(() => {
    const s = new Set(ciclos.map((c) => c.anio).filter(Boolean));
    return [...s].sort((a, b) => b - a);
  }, [ciclos]);

  const semanasEnDatos = useMemo(() => {
    const s = new Set(ciclos.map((c) => c.numeroSemana).filter((n) => n != null));
    return [...s].sort((a, b) => a - b);
  }, [ciclos]);

  const ciclosFiltrados = useMemo(() => {
    const q = filtroTexto.trim().toLowerCase();
    return ciclos.filter((c) => {
      if (q && !String(c.nombre || '').toLowerCase().includes(q)) return false;
      if (filtroProyectoId && String(c.proyectoId || '') !== String(filtroProyectoId)) return false;
      if (filtroPlantillaId && String(c.plantillaId || '') !== String(filtroPlantillaId)) return false;
      if (filtroAnio) {
        const y = parseInt(filtroAnio, 10);
        if (!Number.isNaN(y) && c.anio !== y) return false;
      }
      if (filtroNumeroSemana) {
        const w = parseInt(filtroNumeroSemana, 10);
        if (!Number.isNaN(w) && c.numeroSemana !== w) return false;
      }
      return true;
    });
  }, [ciclos, filtroTexto, filtroProyectoId, filtroPlantillaId, filtroAnio, filtroNumeroSemana]);

  const hayFiltrosActivos =
    Boolean(filtroTexto.trim()) ||
    Boolean(filtroProyectoId) ||
    Boolean(filtroPlantillaId) ||
    Boolean(filtroAnio) ||
    Boolean(filtroNumeroSemana);

  const limpiarFiltros = () => {
    setFiltroTexto('');
    setFiltroProyectoId('');
    setFiltroPlantillaId('');
    setFiltroAnio('');
    setFiltroNumeroSemana('');
  };

  const nombrePlantilla = (pid) =>
    plantillas.find((p) => String(p.id) === String(pid))?.nombre || pid || '—';

  const nombreProyecto = (proyectoId) => nombreProyectoDesdeLista(proyectoId, proyectos);

  const eliminarCiclo = async (c) => {
    const msg = `¿Eliminar el cíclico "${c.nombre}"?\n\nSe borrarán también las salidas a producción guardadas para este ciclo. Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      await deleteCiclico(c.id);
      eliminarSalidasPorCicloId(c.id);
      const bundle = await fetchDatosPlaneamientoCiclicos();
      setCiclos(bundle.ciclos);
    } catch (e) {
      window.alert(e.message || 'No se pudo eliminar el cíclico.');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">Planeamiento</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Cíclicos</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cíclicos</h1>
          <p className="mt-1 text-sm text-gray-600">
            Listado de ciclos configurados por semana. Cada registro usa una plantilla cíclica y guarda el periodo y la
            fecha en que se configuró.
          </p>
        </div>
        <Link
          to="/produccion-concesionaria/planeamiento/plantillas-ciclicos"
          className="text-sm text-teal-700 font-medium hover:underline shrink-0 self-start"
        >
          Plantillas cíclicos →
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-700">Ciclos configurados</span>
          <div className="flex flex-wrap items-center gap-3">
            {!datosLoading && ciclos.length > 0 && (
              <span className="text-xs text-gray-500">
                Mostrando <strong className="text-gray-700">{ciclosFiltrados.length}</strong> de {ciclos.length}
              </span>
            )}
            <Link
              to="/produccion-concesionaria/planeamiento/ciclicos/nuevo"
              className="inline-flex items-center rounded-md bg-teal-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-teal-700 transition-colors"
            >
              + Nuevo cíclico
            </Link>
          </div>
        </div>

        {datosLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Cargando datos del servidor…</div>
        ) : (
          <>
            {ciclos.length > 0 && (
              <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/50">
                <p className="text-xs font-medium text-gray-600 mb-3">Filtros</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  <div className="sm:col-span-2 lg:col-span-1 xl:col-span-2">
                    <label htmlFor="filtro-ciclo-nombre" className="block text-xs font-medium text-gray-600 mb-1">
                      Buscar por nombre
                    </label>
                    <input
                      id="filtro-ciclo-nombre"
                      type="search"
                      value={filtroTexto}
                      onChange={(e) => setFiltroTexto(e.target.value)}
                      placeholder="Nombre del cíclico…"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="filtro-proyecto" className="block text-xs font-medium text-gray-600 mb-1">
                      Proyecto
                    </label>
                    <select
                      id="filtro-proyecto"
                      value={filtroProyectoId}
                      onChange={(e) => setFiltroProyectoId(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Todos</option>
                      {proyectos.map((p) => {
                        const base = p.code ? `${p.code} — ${p.name}` : p.name;
                        const label = p.type === 'operacion' ? `${base} (operación)` : base;
                        return (
                          <option key={p.id} value={p.id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="filtro-plantilla" className="block text-xs font-medium text-gray-600 mb-1">
                      Plantilla
                    </label>
                    <select
                      id="filtro-plantilla"
                      value={filtroPlantillaId}
                      onChange={(e) => setFiltroPlantillaId(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Todas</option>
                      {plantillas.map((pl) => (
                        <option key={pl.id} value={pl.id}>
                          {pl.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="filtro-anio" className="block text-xs font-medium text-gray-600 mb-1">
                      Año
                    </label>
                    <select
                      id="filtro-anio"
                      value={filtroAnio}
                      onChange={(e) => setFiltroAnio(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Todos</option>
                      {aniosEnDatos.map((a) => (
                        <option key={a} value={String(a)}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="filtro-semana" className="block text-xs font-medium text-gray-600 mb-1">
                      N.º semana
                    </label>
                    <select
                      id="filtro-semana"
                      value={filtroNumeroSemana}
                      onChange={(e) => setFiltroNumeroSemana(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Todas</option>
                      {semanasEnDatos.map((n) => (
                        <option key={n} value={String(n)}>
                          Semana {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {hayFiltrosActivos && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={limpiarFiltros}
                      className="text-sm text-teal-700 font-medium hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  </div>
                )}
              </div>
            )}

            {ciclos.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No hay ciclos registrados.</div>
            ) : ciclosFiltrados.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Ningún ciclo coincide con los filtros.{' '}
                <button type="button" onClick={limpiarFiltros} className="text-teal-700 font-medium hover:underline">
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80 text-left text-gray-600">
                      <th className="px-4 py-3 font-medium">Cíclico</th>
                      <th className="px-4 py-3 font-medium">Proyecto</th>
                      <th className="px-4 py-3 font-medium">Plantilla</th>
                      <th className="px-4 py-3 font-medium">Semana</th>
                      <th className="px-4 py-3 font-medium">Periodo</th>
                      <th className="px-4 py-3 font-medium">Configurado el</th>
                      <th className="px-4 py-3 font-medium min-w-[200px]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ciclosFiltrados.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 font-medium text-gray-900">{c.nombre}</td>
                        <td className="px-4 py-3 text-gray-700">{nombreProyecto(c.proyectoId)}</td>
                        <td className="px-4 py-3 text-gray-700">{nombrePlantilla(c.plantillaId)}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {etiquetaSemana(c.anio, c.numeroSemana)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {c.fechaInicio && c.fechaFin
                            ? formatearRangoFechas(c.fechaInicio, c.fechaFin)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{formatearFechaHora(c.fechaCreacion)}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/produccion-concesionaria/planeamiento/ciclicos/${c.id}`}
                              className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-800 hover:bg-teal-100"
                            >
                              Detalle
                            </Link>
                            <button
                              type="button"
                              onClick={() => eliminarCiclo(c)}
                              className="inline-flex items-center rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6">
        <Link to="/produccion-concesionaria" className="text-teal-700 text-sm font-medium hover:underline">
          ← Producción concesionaria
        </Link>
      </div>
    </div>
  );
};

export default CiclicosIndex;
